import {useCallback, useLayoutEffect, useRef, useState} from 'react';

type Item = Record<string, any>;

export interface UseLazyLoadOptions<TItem extends Item> {
  getItems: (options: {
    trigger: 'hard-refresh' | 'soft-refresh' | 'load' | 'load-more';
    pageNumber: number;
    pageSize: number;
  }) => Promise<TItem[]>;
  pageSize: number;
  skip?: boolean;
}

export const useLazyLoad = <TItem extends Item>({getItems, pageSize, skip}: UseLazyLoadOptions<TItem>) => {
  const [items, setItems] = useState<TItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadCount, setLoadCount] = useState(0);
  const [loadMoreCount, setLoadMoreCount] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);

  const lastLoadId = useRef(-1);
  const lastLoadMoreId = useRef(-1);
  const lastRefreshId = useRef(-1);
  const lastPage = useRef(0);
  const hasMore = useRef(false);

  const load = useCallback(async () => {
    const loadId = Math.random();
    lastLoadId.current = loadId;
    lastLoadMoreId.current = -1;
    lastRefreshId.current = -1;
    lastPage.current = 1;
    setLoadCount((value) => value + 1);
    try {
      const items = await getItems({pageNumber: 1, pageSize, trigger: 'load'});
      if (lastLoadId.current === loadId) {
        hasMore.current = items.length >= pageSize;
        setItems([...items]);
      }
    } catch (e) {
    } finally {
      setLoadCount((value) => value - 1);
      setIsInitialized(true);
    }
  }, [getItems, pageSize]);

  const loadMore = async () => {
    if (hasMore.current && lastLoadMoreId.current === -1) {
      lastLoadMoreId.current = Math.random();
      setLoadMoreCount((value) => value + 1);
      try {
        const newItems = await getItems({pageNumber: lastPage.current + 1, pageSize, trigger: 'load-more'});
        if (lastLoadMoreId.current !== -1) {
          hasMore.current = newItems.length >= pageSize;
          lastPage.current += 1;
          setItems((items) => [...items, ...newItems]);
        }
      } catch (e) {
      } finally {
        lastLoadMoreId.current = -1;
        setLoadMoreCount((value) => value - 1);
      }
    }
  };

  const refresh = useCallback(
    async (hard = true) => {
      const refreshId = Math.random();
      lastRefreshId.current = refreshId;
      setRefreshCount((refreshCount) => refreshCount + 1);
      try {
        const items = await getItems({
          pageNumber: 1,
          pageSize: lastPage.current * pageSize,
          trigger: hard ? 'hard-refresh' : 'soft-refresh',
        });
        if (lastRefreshId.current === refreshId) {
          hasMore.current = items.length >= lastPage.current * pageSize;
          lastPage.current = Math.ceil(items.length / pageSize);
          setItems(items);
        }
      } catch (e) {
        console.log('error', e);
      } finally {
        setRefreshCount((refreshCount) => refreshCount - 1);
      }
    },
    [getItems, pageSize],
  );

  useLayoutEffect(() => {
    if (!skip) {
      void load();
    }
  }, [load, skip]);

  return {
    isInitialized,
    isLoading: loadCount > 0,
    isLoadingMore: loadMoreCount > 0,
    isRefreshing: refreshCount > 0,
    items,
    load,
    loadMore,
    refresh,
  };
};
